// Imports => MOBX
import { observable, computed, makeObservable, action, toJS } from 'mobx';
import { getDataSource } from '../config/store.config';

let app = {};

export class ThemesStore {
  constructor(store) {
    makeObservable(this);
    app.store = store;
    this.dataSource = getDataSource('themes');
  }

  @observable
  items = [];

  @observable
  single = {};

  @observable
  loading = {
    status: false,
    message: undefined,
  };

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @computed
  get all_themes() {
    return this.items ? toJS(this.items) : [];
  }

  @computed
  get get_single() {
    return this.single ? toJS(this.single) : null;
  }

  @action
  resetTheme = () => {
    this.single = {};
  };

  @action
  setLoadingStatus = (status) => {
    this.loading.status = status;
  };

  @action
  setThemes = (items) => {
    this.items = items;
  };

  @action
  setTheme = (theme) => {
    this.single = theme;
  };

  @action
  fetchTheme = async (slug) => {
    this.loading.status = true;

    try {
      // First check if we already have themes loaded
      if (this.items.length > 0) {
        const theme = this.items.find((item) => item.slug === slug);
        if (theme) {
          this.setTheme(theme);
          return;
        }
      }

      // Try to fetch using the API
      if (app.store?.api?.themes) {
        try {
          // For API calls, we need to convert slug to ID or implement a slug-based endpoint
          const response = await app.store.api.themes.single(slug);
          this.setTheme(response);
          return;
        } catch (apiError) {
          console.error('API error:', apiError);
        }
      }

      // If API fails or is not available, try to use mock data as fallback
      if (this.dataSource) {
        const theme = await this.dataSource.getBySlug(slug);
        this.setTheme(theme);
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading.status = false;
    }
  };

  @action
  fetchThemes = async () => {
    this.loading.status = true;

    try {
      // Use the themes API to fetch themes if available
      if (app.store?.api?.themes) {
        const response = await app.store.api.themes.all();
        this.setThemes(response.results || []);
      }
      // Fall back to mock data if API not available
      else if (this.dataSource) {
        const themes = await this.dataSource.getAll();
        this.setThemes(themes);
      }
    } catch (e) {
      console.error(e);
      // If API fails, try to use mock data as fallback
      if (this.dataSource) {
        try {
          const themes = await this.dataSource.getAll();
          this.setThemes(themes);
        } catch (fallbackError) {
          console.error('Fallback error:', fallbackError);
          this.setThemes([]);
        }
      } else {
        this.setThemes([]);
      }
    } finally {
      this.setLoadingStatus(false);
    }
  };
}

export default ThemesStore;
